/**
 * RoamerLearningEngine.ts — Engine #6: the Roamer Learning Engine™.
 *
 * Continuously improves personalization by learning from behavior. It NEVER
 * makes decisions — it only emits learning signals that other engines
 * (Intelligence, Mission, Score, Local, Workspace, Journey, future AI) consume.
 *
 * Guarantees:
 *  • Every learned preference has confidence, lastUpdated, supporting
 *    behaviors, an explanation, and a manual-override status.
 *  • Preferences are never overwritten — the value history is kept, and
 *    confidence is continuously re-derived from the full (decayed) evidence.
 *  • Manual overrides always win and are never relearned over until cleared.
 *  • Privacy is first-class: view / reset / disable / delete / export.
 *  • Deterministic: all inference is a pure function of (events, now).
 */

import type {
  BehavioralAction,
  BehavioralEvent,
  BehaviorCategory,
  LearnedPreference,
  LearningProfile,
  LearningSignal,
  PreferenceKey,
  PreferenceValue,
} from './models';
import { evidenceToConfidence, INFERENCE_RULES } from './rules';
import { quotaSafeStorage } from '@/services/storage/QuotaSafeStorage';

const STORAGE_KEY = 'nomad:learning:v1';
quotaSafeStorage.registerManagedKey(STORAGE_KEY, 'Learning Engine');
const MAX_EVENTS = 2000;

interface PersistedState {
  events: BehavioralEvent[];
  overrides: Partial<Record<PreferenceKey, PreferenceValue>>;
  valueHistory: Partial<Record<PreferenceKey, Array<{ value: PreferenceValue; at: number }>>>;
  lastUpdatedByKey: Partial<Record<PreferenceKey, number>>;
  learningEnabled: boolean;
}

export interface RecordEventInput {
  action: BehavioralAction;
  category: BehaviorCategory;
  targetId: string;
  label: string;
  attributes?: Record<string, string | number | boolean>;
  at?: number;
}

type ProfileListener = (profile: LearningProfile) => void;

export class RoamerLearningEngine {
  private events: BehavioralEvent[] = [];
  private overrides: Partial<Record<PreferenceKey, PreferenceValue>> = {};
  private valueHistory: Partial<Record<PreferenceKey, Array<{ value: PreferenceValue; at: number }>>> = {};
  private lastUpdatedByKey: Partial<Record<PreferenceKey, number>> = {};
  private learningEnabled = true;
  private listeners = new Set<ProfileListener>();
  private eventSeq = 0;

  constructor() {
    this.load();
  }

  // ── Observation ─────────────────────────────────────────────────────────────

  /** Record one behavioral event. No-op when learning is disabled (privacy). */
  recordEvent(input: RecordEventInput): BehavioralEvent | null {
    if (!this.learningEnabled) return null;
    const at = input.at ?? Date.now();
    const event: BehavioralEvent = {
      id: `evt-${at}-${this.eventSeq++}`,
      action: input.action,
      category: input.category,
      targetId: input.targetId,
      label: input.label,
      attributes: { ...(input.attributes ?? {}) },
      at,
    };
    this.events.push(event);
    if (this.events.length > MAX_EVENTS) this.events.shift();
    this.syncInference(at);
    this.persist();
    this.notify(at);
    return event;
  }

  // ── Inference / output ──────────────────────────────────────────────────────

  /** The full evolving profile — every preference explainable, never opaque. */
  getProfile(now: number = Date.now()): LearningProfile {
    this.syncInference(now); // capture decay-only transitions at read time too
    const preferences: LearnedPreference[] = [];
    for (const key of Object.keys(INFERENCE_RULES) as PreferenceKey[]) {
      const pref = this.inferPreference(key, now);
      if (pref) preferences.push(pref);
    }
    return {
      preferences,
      eventCount: this.events.length,
      learningEnabled: this.learningEnabled,
      updatedAt: now,
    };
  }

  getPreference(key: PreferenceKey, now: number = Date.now()): LearnedPreference | null {
    this.syncInference(now);
    return this.inferPreference(key, now);
  }

  /**
   * Learning signals for consuming engines: effective value (override wins),
   * confidence, and the explanation. Empty when learning is disabled.
   */
  getSignals(now: number = Date.now()): LearningSignal[] {
    if (!this.learningEnabled) return [];
    return this.getProfile(now).preferences.map((p) => ({
      key: p.key,
      value: p.overridden && p.overrideValue != null ? p.overrideValue : p.value,
      confidence: p.overridden ? 100 : p.confidence,
      source: p.overridden ? 'override' : 'learned',
      explanation: structuredClone(p.explanation),
    }));
  }

  private inferPreference(key: PreferenceKey, now: number): LearnedPreference | null {
    const override = this.overrides[key];
    const result = INFERENCE_RULES[key](this.events, now);
    if (result == null && override == null) return null;
    const history = [...(this.valueHistory[key] ?? [])];
    const lastUpdated = this.lastUpdatedByKey[key] ?? now;
    if (result == null) {
      // Override with no learned evidence yet.
      return {
        key,
        value: override!,
        confidence: 100,
        lastUpdated,
        supportingBehaviors: [],
        explanation: { summary: 'Set manually by you.', reasons: ['You set this preference yourself.'] },
        overridden: true,
        overrideValue: override!,
        history,
      };
    }
    return {
      key,
      value: result.value,
      confidence: evidenceToConfidence(result.evidence),
      lastUpdated,
      supportingBehaviors: [...result.supportingBehaviors],
      explanation: {
        summary: override != null
          ? 'Set manually by you (learned evidence shown for transparency).'
          : `Learned from ${result.supportingBehaviors.length} of your interactions.`,
        reasons: [...result.reasons],
      },
      overridden: override != null,
      overrideValue: override ?? null,
      history,
    };
  }

  /**
   * Preferences are never overwritten — keep the trail of learned values and a
   * true per-key lastUpdated. Runs on BOTH event ingestion and reads, so
   * transitions caused purely by evidence decay/expiry (no new events) are
   * captured too. Persists only when something actually changed.
   */
  private syncInference(now: number): void {
    let changed = false;
    for (const key of Object.keys(INFERENCE_RULES) as PreferenceKey[]) {
      const result = INFERENCE_RULES[key](this.events, now);
      if (result == null) continue;
      const trail = this.valueHistory[key] ?? [];
      const last = trail[trail.length - 1];
      if (!last || last.value !== result.value) {
        trail.push({ value: result.value, at: now });
        this.valueHistory[key] = trail;
        this.lastUpdatedByKey[key] = now;
        changed = true;
      }
    }
    if (changed) this.persist();
  }

  // ── Manual override (users always win) ──────────────────────────────────────

  overridePreference(key: PreferenceKey, value: PreferenceValue, now: number = Date.now()): void {
    this.overrides[key] = value;
    this.lastUpdatedByKey[key] = now;
    this.persist();
    this.notify(now);
  }

  clearOverride(key: PreferenceKey, now: number = Date.now()): void {
    delete this.overrides[key];
    this.lastUpdatedByKey[key] = now;
    this.persist();
    this.notify(now);
  }

  // ── Privacy controls ────────────────────────────────────────────────────────

  /** Disable/enable behavioral learning entirely. */
  setLearningEnabled(enabled: boolean, now: number = Date.now()): void {
    this.learningEnabled = enabled;
    this.persist();
    this.notify(now);
  }

  isLearningEnabled(): boolean {
    return this.learningEnabled;
  }

  /** Reset learned preferences (keeps raw history unless deleted separately). */
  resetPreferences(now: number = Date.now()): void {
    this.overrides = {};
    this.valueHistory = {};
    this.lastUpdatedByKey = {};
    this.persist();
    this.notify(now);
  }

  /** Delete the entire learning history — events, overrides, value trails. */
  deleteHistory(now: number = Date.now()): void {
    this.events = [];
    this.overrides = {};
    this.valueHistory = {};
    this.lastUpdatedByKey = {};
    this.persist();
    this.notify(now);
  }

  /** Export the preference profile as portable JSON (user-owned data). */
  exportProfile(now: number = Date.now()): string {
    return JSON.stringify(
      { exportedAt: now, profile: this.getProfile(now), events: this.events },
      null,
      2,
    );
  }

  // ── Subscription (how other engines stay current without polling) ──────────

  onProfileChanged(listener: ProfileListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(now: number): void {
    if (this.listeners.size === 0) return;
    const profile = this.getProfile(now);
    this.listeners.forEach((l) => {
      try {
        l(profile);
      } catch {
        // one broken subscriber never breaks the engine
      }
    });
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    const state: PersistedState = {
      events: this.events,
      overrides: this.overrides,
      valueHistory: this.valueHistory,
      lastUpdatedByKey: this.lastUpdatedByKey,
      learningEnabled: this.learningEnabled,
    };
    // BETA GATE HARDENING™: quota-safe write — on quota pressure the persisted
    // copy sheds oldest evidence first (in-memory state and APIs unchanged).
    quotaSafeStorage.writeJSON(STORAGE_KEY, state, (s, level) => {
      const keepEvents = level === 1 ? 1000 : 400;
      const keepHistory = level === 1 ? 50 : 10;
      const valueHistory: PersistedState['valueHistory'] = {};
      for (const [key, arr] of Object.entries(s.valueHistory ?? {}) as Array<
        [keyof PersistedState['valueHistory'], PersistedState['valueHistory'][keyof PersistedState['valueHistory']]]
      >) {
        valueHistory[key] = Array.isArray(arr) ? arr.slice(-keepHistory) : arr;
      }
      return { ...s, events: s.events.slice(-keepEvents), valueHistory };
    });
  }

  private load(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as PersistedState;
      this.events = Array.isArray(state.events) ? state.events : [];
      this.overrides = state.overrides ?? {};
      this.valueHistory = state.valueHistory ?? {};
      this.lastUpdatedByKey = state.lastUpdatedByKey ?? {};
      this.learningEnabled = state.learningEnabled !== false;
    } catch {
      // corrupt state — start fresh rather than crash
    }
  }
}

/** App-wide singleton. */
export const roamerLearningEngine = new RoamerLearningEngine();
